import {Drawer} from '@mantine/core';
import {type FC, forwardRef, useCallback} from 'react';
import {MdFilterList} from 'react-icons/md';
import {cn} from '../../../../../lib/classname';
import {useBooleanState} from '../../../../../shared/services/useBooleanState';
import {
  SP_FILTER_CONFIG,
  type SpStatusFilter,
} from '../../../usecase/useProgressStatusFilter';

type Props = {
  selectedStatus: SpStatusFilter;
  onStatusChange: (status: SpStatusFilter) => void;
  className?: string;
};

/**
 * 選考管理の進捗ステータスフィルターコンポーネント（SP版）
 * ドロワー形式でステータス選択UIを提供する
 */
export const ProgressStatusFilter: FC<Props> = ({
  selectedStatus,
  onStatusChange,
  className,
}) => {
  const [shown, show, hide] = useBooleanState(false);

  const onClickHandler = useCallback(() => {
    show();
  }, [show]);

  const onCloseHandler = useCallback(() => {
    hide();
  }, [hide]);

  const handleStatusSelect = useCallback(
    (status: SpStatusFilter) => {
      onStatusChange(status);
      hide();
    },
    [onStatusChange, hide],
  );

  const filterContent = (
    <div className="flex flex-col gap-8 p-16">
      <div className="font-bold text-sm mb-8">ステータス</div>
      {Object.entries(SP_FILTER_CONFIG).map(([key, config]) => {
        const status = key as SpStatusFilter;
        const isSelected = selectedStatus === status;

        return (
          <button
            key={key}
            type="button"
            onClick={() => handleStatusSelect(status)}
            className={cn(
              'text-left py-12 px-16 rounded-8 text-sm',
              isSelected
                ? 'bg-primary text-white'
                : 'bg-background hover:bg-background-1',
            )}
          >
            {config.title}
          </button>
        );
      })}
    </div>
  );

  // 'all'選択時は背景色を変更
  const isAllSelected = selectedStatus === 'all';
  const buttonStyles = cn(
    'py-8 px-12 gap-[6px] flex items-center text-base rounded-full relative',
    isAllSelected ? 'bg-background text-black-1' : 'bg-primary text-white',
    className,
  );

  return (
    <>
      <FilterButton
        selectedStatusTitle={SP_FILTER_CONFIG[selectedStatus].title}
        onClickHandler={onClickHandler}
        className={buttonStyles}
      />
      <Drawer
        opened={shown}
        onClose={onCloseHandler}
        position="bottom"
        size="auto"
        overlayProps={{opacity: 0.48}}
        withCloseButton={false}
        className="p-16"
        styles={{inner: {left: 0}}}
      >
        <div className="text-black-4 mb-16">ステータスで絞り込み</div>
        {filterContent}
      </Drawer>
    </>
  );
};

type FilterButtonProps = {
  selectedStatusTitle: string;
  onClickHandler: () => void;
  className?: string;
};

const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(
  ({selectedStatusTitle, onClickHandler, className}, ref) => {
    return (
      <button
        type="button"
        ref={ref}
        className={className}
        onClick={onClickHandler}
      >
        <MdFilterList size={20} />
        <span className="text-sm">{selectedStatusTitle}</span>
      </button>
    );
  },
);

FilterButton.displayName = 'FilterButton';
